import { Edge, Node } from 'reactflow';
import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import {
  CiaSubLayers,
  InitiativesSubLayerTypes,
  SubLayerTypes,
} from '@constants/canvas/layers';
import { matchesModelFiltersDto } from '@context/ProjectFilters/matchesModelFilters';
import { questionnaireViewFilters } from '@context/ProjectFilters/questionnaireViewFilters';
import { getModelFiltersState } from '@store/zustand/model-filters';
import useQuestionnaireResultStore from '@store/zustand/questionnaire-result';

/**
 * Id-addressed view of the canvas graph, for lookups that happen outside React.
 *
 * The maps are built on read and cached against the identity of the array they
 * came from, so a write nothing looks up costs nothing. Correct because the
 * store replaces `nodes` and `edges` rather than mutating them.
 */
class CanvasMap {
  private cachedNodes: Node[] | null = null;

  private cachedNodeMap = new Map<string, Node>();

  private cachedRelations: Edge[] | null = null;

  private cachedRelationMap = new Map<string, Edge>();

  get nodes(): Node[] {
    return useStore.getState().nodes;
  }

  get relations(): Edge[] {
    return useStore.getState().edges;
  }

  get nodeMap(): Map<string, Node> {
    const { nodes } = this;

    if (nodes !== this.cachedNodes) {
      this.cachedNodes = nodes;
      this.cachedNodeMap = new Map(nodes.map((node) => [node.id, node]));
    }

    return this.cachedNodeMap;
  }

  get relationMap(): Map<string, Edge> {
    const { relations } = this;

    if (relations !== this.cachedRelations) {
      this.cachedRelations = relations;
      this.cachedRelationMap = new Map(
        relations.map((edge) => [edge.id, edge]),
      );
    }

    return this.cachedRelationMap;
  }

  /**
   * Whether the model filters hide this node, for the collapse routing that
   * runs outside React.
   *
   * One call of the shared predicate, read through the store's vanilla
   * accessor, so the verdict is the canvas's own under any combination of
   * categories. Chrome nodes and ids the canvas does not hold are never
   * hidden. The questionnaire result keeps the per-node rules the canvas
   * applies in that view — the selection `questionnaireViewFilters` gives the
   * entity, and a CIA-layer entity or Change Initiative that is unreported or
   * incomplete is hidden — but not its cascades, which need the whole graph.
   */
  isNodeHiddenByFilters(id: string): boolean {
    const node = this.nodeMap.get(id);

    if (!node || node.type !== NodesTypes.CustomNode) return false;

    const { result: questionnaireResult } =
      useQuestionnaireResultStore.getState();

    const dto = node.data?.dto;
    const subLayer = node.parentNode as SubLayerTypes;

    const isReportLayer = [
      ...CiaSubLayers,
      InitiativesSubLayerTypes.ChangeInitiatives,
    ].includes(subLayer as any);

    if (
      questionnaireResult &&
      isReportLayer &&
      (dto?.is_reported === false || !dto?.is_complete)
    ) {
      return true;
    }

    return !matchesModelFiltersDto(
      dto,
      subLayer,
      questionnaireViewFilters(
        getModelFiltersState(),
        dto,
        subLayer,
        Boolean(questionnaireResult),
      ),
    );
  }
}

export const canvasMap = new CanvasMap();
